'use client'

import type { ReactNode } from 'react'

import { LoadingOverlay } from '@/app/components/shared/LoadingOverlay'
import { usePageLoadingOverlay } from '@/app/context/pageLoading'
import { useGeoLocationSync } from '@/app/lib/hooks/useGeoLocationSync'
import { route, routes } from '@/app/lib/routing'

import type { GeoLocationRouteName, GeoLocationSyncProps } from './GeoLocationSync.types'

/**
 * Mapa de builders disponibles para esta feature — vive en cliente (este
 * componente ya es `'use client'`), así que aquí sí es seguro referenciar las
 * funciones de `routes.*` directamente (a diferencia del Server Component que
 * monta este componente, que solo puede pasar el nombre — ver
 * GeoLocationSync.types.ts). No incluye `'category'`: esa ruta necesita el
 * `categorySlug` de la página (variable), así que se construye aparte, en
 * `GeoLocationSync` mismo.
 */
const ROUTE_BUILDERS: Readonly<Record<Exclude<GeoLocationRouteName, 'category'>, (location: string) => string>> = {
  agenda: routes.agenda,
  recintos: routes.recintos,
  loMasVendido: routes.loMasVendido,
  home: routes.homeByLocation,
  promociones: routes.promociones,
}

/**
 * Punto de montaje headless y reutilizable para la geolocalización automática
 * (spec.md, contracts/geo-location-sync-component.md). Por defecto renderiza
 * `null` — delega el 100% de su lógica a `useGeoLocationSync`, para poder
 * añadirse a cualquier página web-general en alcance sin duplicar el
 * `'use client'`/import del hook en cada una (Constitución III). Solo pinta
 * el `LoadingOverlay` cuando el consumidor lo pide explícitamente
 * (`showLoadingOverlay`, ver GeoLocationSync.types.ts).
 */
export function GeoLocationSync({
  location,
  routeName,
  categorySlug,
  subcategorySlug,
  showLoadingOverlay = false,
  loadingLabel,
}: GeoLocationSyncProps): ReactNode {
  // `routeName === 'category'` no tiene un builder fijo en `ROUTE_BUILDERS`
  // (el slug de categoría — y, en la PLP de subcategoría, también el de
  // subcategoría — varía por página): se construye aquí. Con
  // `subcategorySlug` reutiliza `routes.subcategoria` (misma fórmula que ya
  // usa `SubcategoriaFilters` para su propio filtro); sin él, `route()`
  // simple, igual que ya hace `Explora` para sus propios tiles de categoría.
  const buildLocalizedHref =
    routeName === 'category'
      ? (target: string) =>
          subcategorySlug
            ? routes.subcategoria(categorySlug!, target, subcategorySlug)
            : route(`/${categorySlug}/${target}`)
      : ROUTE_BUILDERS[routeName]
  const { isPending } = useGeoLocationSync({ location, buildLocalizedHref })
  const active = showLoadingOverlay && isPending
  const label = loadingLabel ?? 'Cargando'
  // Se registra en `PageLoadingProvider` (layout de la web general) en vez de
  // pintar su propio overlay: así, si esta redirección termina justo cuando
  // otro consumidor de la página (p. ej. `RecentlyViewed`) empieza el suyo, se
  // perciben como un único spinner continuo en vez de dos parpadeos seguidos
  // — el provider sobrevive al `router.replace()` que desmonta este
  // componente, cosa que un overlay pintado aquí mismo no podría.
  const { standalone } = usePageLoadingOverlay(active, label)

  if (standalone && active) {
    return <LoadingOverlay label={label} />
  }
  return null
}
